import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { routes, cookieDomain } from '../config';
import { closePopup } from '../utils/popupManager';

// Module-level variables
let isListenerInitialized = false;
let sessionCheckTimeoutId = null;
let isMcpLoaded = false;
const subscribers = new Set();

// Notification function to update all subscribers
const notifySubscribers = () => {
  subscribers.forEach((callback) => callback());
};

/**
 * Custom hook to initialize global MCP event listener
 */
const useMcpEvents = () => {
  const [mcpLoaded, setMcpLoaded] = useState(isMcpLoaded);

  useEffect(() => {
    const initializeGlobalListener = () => {
      if (isListenerInitialized) return;

      window.addEventListener('message', (e) => {
        if (e.origin !== routes.mcp.domain) return;
        if (typeof e.data === 'object') {
          if (
            'pageEvent' in e.data &&
            ['pageLoaded', 'providerError'].includes(
              e.data.pageEvent.type,
            )
          ) {
            console.log(
              '[MCP Session Check] MCP Post Message Received',
            );

            // directly using js-cookie due to cookie service function not supporting
            // some options yet, needs to be updated
            let hasLiveSession = false;
            const livesessionValue = Cookies.get('livesession');
            if (livesessionValue) {
              const timestamp = parseInt(livesessionValue, 10);
              const timeDifference = Date.now() - timestamp;
              const maxAge = 35 * 60 * 1000; // 35 minutes in milliseconds

              hasLiveSession =
                !Number.isNaN(timestamp) && timeDifference <= maxAge;
            }

            if (!hasLiveSession) {
              Cookies.set('livesession', Date.now().toString(), {
                sameSite: 'none',
                domain: cookieDomain,
                path: '/',
                secure: true,
              });
            }
            isMcpLoaded = true;
            notifySubscribers();

            if (closePopup()) {
              // if there's a popup, it will be closed
              // refresh the page
              window.location.reload();
            }

            // Clear timeout since we received the message
            if (sessionCheckTimeoutId) {
              clearTimeout(sessionCheckTimeoutId);
              sessionCheckTimeoutId = null;
            }
          }
        }
      });

      // Set timeout to check for MCP session
      sessionCheckTimeoutId = setTimeout(() => {
        console.log(
          '[MCP Session Check] MCP postMessage not received after 15 seconds',
        );
        // Dispatch an event that can be listened to elsewhere
        window.dispatchEvent(new CustomEvent('mcpSessionTimeout'));
      }, 15000); // 15 seconds

      isListenerInitialized = true;
      console.log(
        '[MCP Session Check] Global MCP event listener initialized',
      );
    };

    // Initialize the listener when the hook is first used
    initializeGlobalListener();

    // Subscribe to mcpLoaded state changes
    const handleMcpLoadedChange = () => {
      setMcpLoaded(isMcpLoaded);
    };

    subscribers.add(handleMcpLoadedChange);

    // Do not cleanup the listener as it is meant to be global
    // Cleanup subscription when component unmounts
    return () => {
      subscribers.delete(handleMcpLoadedChange);
    };
  }, []);

  return { mcpLoaded };
};

export default useMcpEvents;
